import { Box, debounce, Skeleton, Stack, Typography } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { SearchBar } from 'src/components/SearchBar/search-bar';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { Address, Client, Conversation } from 'src/graphql';
import { DEFAULT_PAGINATION_LIMIT, useSearch } from 'src/graphql/entities/search/use-search';

import { ConditionInput, EntityNames } from 'src/graphql/types';
import { ConversationInfo, ConversationTooltipContent } from './conversation-info';
import { ConversationTooltip } from './conversation-tooltip';
import {
  TOOLTIP_ENTER_DELAY,
  TOOLTIP_LEAVE_DELAY,
  useConversationTooltip,
} from './use-conversation-tooltip';

interface RecentConversationsProps {
  conversation?: Conversation;
  address?: Partial<Address>;
  client?: Partial<Client>;
}

const SKELETON_ITEMS = 5;

export const ConversationInfoSkeleton = () => (
  <Stack direction="row" spacing={3} alignItems="center">
    <Skeleton variant="circular" width={20} height={20} />
    <Stack
      direction="row"
      width="calc(100% - 15px)"
      justifyContent="space-between"
      spacing={2}
    >
      <Skeleton width="70%" />
      <Skeleton width="30%" />
    </Stack>
  </Stack>
);

export const RecentConversations = ({
  conversation,
  address,
  client,
}: RecentConversationsProps) => {
  const baseFilters: ConditionInput[] = useMemo(() => {
    const filters: ConditionInput[] = [];

    if (address?.addressId) {
      filters.push({
        attribute: 'associatedAddresses/addressId',
        operator: '=',
        value: address.addressId,
      });
    }

    if (client?.clientId) {
      filters.push({
        attribute: 'requester/participantIdentity',
        operator: '=',
        value: client.clientId,
        logicalOperator: 'or',
      });
      filters.push({
        attribute: 'associatedAddresses/clients/clientId',
        operator: '=',
        value: client.clientId,
        logicalOperator: 'or',
      });
    }

    if (conversation?.conversationId) {
      filters.push({
        attribute: 'conversationId',
        operator: '!=',
        value: conversation.conversationId,
      });
    }

    return filters;
  }, [address?.addressId, client?.clientId, conversation?.conversationId]);

  const [searchTerm, setSearchTerm] = useState('');
  const tooltip = useConversationTooltip();

  const {
    records: conversations,
    loadMore,
    isLoading,
    hasMore,
    isLoadingMore,
  } = useSearch({
    entityName: EntityNames.Conversation,
    searchTerm,
    filter: baseFilters,
    limit: DEFAULT_PAGINATION_LIMIT * 3,
    skip: !address && !client,
    showResultsWhenEmptySearchTerm: true,
    sort: [
      { attribute: 'status/sortOrder', desc: false },
      { attribute: 'createdAt', desc: true },
    ],
  }) ?? {};

  const handleOnChange = useMemo(
    () =>
      debounce(({ target }: ChangeEvent) => {
        const value = (target as HTMLInputElement).value;
        setSearchTerm(value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const parentRef = useRef<HTMLDivElement>(null);
  let virtualizerCount = 0;

  if (conversations?.length) {
    virtualizerCount = hasMore ? conversations.length + 1 : conversations.length;
  }

  const rowVirtualizer = useVirtualizer({
    count: virtualizerCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    if (!conversations) {
      return;
    }

    const lastItem = virtualItems[virtualItems.length - 1];

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= conversations.length - 1 && hasMore && !isLoading) {
      void loadMore?.();
    }
  }, [conversations, hasMore, isLoading, loadMore, virtualItems]);

  const tooltipTitle = tooltip.conversation ? (
    <ConversationTooltipContent conversation={tooltip.conversation} />
  ) : (
    ''
  );

  return (
    <Stack spacing={1} flexGrow={1} minHeight={0}>
      <SearchBar onChange={handleOnChange} title="Associated Conversations" />
      <ConversationTooltip
        anchorEl={tooltip.anchorEl}
        enterDelay={TOOLTIP_ENTER_DELAY}
        isOpen={tooltip.isVisible}
        leaveDelay={TOOLTIP_LEAVE_DELAY}
        title={tooltipTitle}
      >
        <Box
          flexGrow={1}
          ref={parentRef}
          sx={{
            marginTop: (theme) => theme.utils.important(0),
            gridAutoRows: 'min-content',
          }}
        >
          {isLoading && !isLoadingMore
            ? Array.from({ length: SKELETON_ITEMS }, (_, i) => (
                <ConversationInfoSkeleton key={i} />
              ))
            : !!conversations && (
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const isLoaderRow = virtualRow.index > conversations.length - 1;
                    const conversation = conversations[virtualRow.index];

                    return (
                      <div
                        key={virtualRow.key}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: `100%`,
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {isLoaderRow && hasMore ? (
                          <Typography variant="body2">Loading more...</Typography>
                        ) : (
                          <ConversationInfo
                            key={`recent-${conversation?.conversationId}`}
                            conversation={conversation}
                            onTooltipOpen={tooltip.open}
                            onTooltipClose={tooltip.close}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

          {!isLoading && !conversations?.length && (
            <Stack padding="0.5rem">
              <Typography alignSelf="center" variant="body2">
                No results
              </Typography>
            </Stack>
          )}
        </Box>
      </ConversationTooltip>
    </Stack>
  );
};
